import React, { useEffect, useState } from 'react'
import { Modal, Label, ModalHeader, ModalBody, FormGroup, Row, Col, Button } from 'reactstrap'
import { Field, Form } from 'react-final-form'
import { flashDispatch, flashMessage, validEmail } from '@/shared/helpers'
import ApiErrorMessage from "@/shared/components/flash/ApiErrorMessage"
import axios from 'axios'
import TemplateButton from "@/shared/components/TemplateButton"
import { connect } from 'react-redux'
import Select from 'react-select';
import { useTranslation } from 'react-i18next'
import { normalizeManagerReportType } from "../../reportRegistry";

const getOrdinal = (n) => {
  if (n >= 11 && n <= 13) return `${n}th`;
  const last = n % 10;
  if (last === 1) return `${n}st`;
  if (last === 2) return `${n}nd`;
  if (last === 3) return `${n}rd`;
  return `${n}th`;
};

const SCHEDULED_FREQUENCY = [
  { label: "Daily",     value: 'Daily'     },
  { label: "Weekly",    value: 'Weekly'    },
  { label: "Monthly",   value: 'Monthly'   },
  { label: "Quarterly", value: 'Quarterly' },
  { label: "Annually",  value: 'Annually'  },
];

const DAY_OPTIONS = {
  Weekly: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    .map(d => ({ label: d, value: d })),
  Monthly: [
    ...Array.from({ length: 28 }, (_, i) => ({ label: `${getOrdinal(i + 1)} day`, value: String(i + 1) })),
    { label: 'Last day', value: 'last' },
  ],
  Quarterly: [
    { label: '1st day of quarter', value: '1'    },
    { label: 'Last day of quarter', value: 'last' },
  ],
  Annually: ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December']
    .map(m => ({ label: `${m} 1st`, value: m })),
};

const sectionTitleStyle = {
  color: 'var(--app-themeColor)',
  fontWeight: 700,
  letterSpacing: '0.05em',
  marginBottom: '1rem',
  fontSize: '0.8rem',
  textTransform: 'uppercase',
};

const AddEditScheduledReportsModal = ({ isOpen, toggle, editData, setTrigger, program, organization, auth }) => {
  const dispatch = flashDispatch();
  const [reportOptions, setReportOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedFrequency, setSelectedFrequency] = useState(null);

  useEffect(() => {
    setSelectedFrequency(
      editData?.frequency ? { label: editData.frequency, value: editData.frequency } : null
    );
  }, [isOpen, editData?.frequency]);
  const { t } = useTranslation();

  const validate = ({ recipient, report_name, frequency, delivery_time, day }) => {
    const errors = {};
    if (!report_name) {
      errors.report_name = t("report_name_is_required");
    }
    if (!frequency) {
      errors.frequency = t("frequency_is_required");
    }
    if (frequency && frequency.value !== 'Daily' && !day) {
      errors.day = 'Day is required';
    }
    if (!delivery_time) {
      errors.delivery_time = 'Delivery time is required';
    }
    if (!recipient || !recipient.trim()) {
      errors.recipient = t("recipient_is_required");
    } else {
      const emails = recipient.split(',').map(e => e.trim()).filter(Boolean);
      if (emails.length === 0) {
        errors.recipient = t("recipient_is_required");
      } else {
        const invalid = emails.filter(email => !validEmail(email));
        if (invalid.length > 0) {
          errors.recipient = t("invalid_email_address");
        }
      }
    }
    return errors;
  };

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await axios.get(`/reports/${program.id}`);
        const options = (response.data || []).map((report) => {
          const normalizedReport = normalizeManagerReportType(report);
          return { label: normalizedReport.title, value: report.report_id };
        });
        setReportOptions(options);
      } catch (error) {
        console.error("Error fetching reports:", error);
      }
    };
    if (program?.id) {
      fetchReports();
    }
  }, [program?.id]);

  const onSubmit = ({ recipient, report_name, frequency, delivery_time, day }) => {
    const payload = {
      program_id: program.id,
      program_report_id: report_name.value,
      recipient: recipient.trim(),
      report_name: report_name.label,
      frequency: frequency.value,
      delivery_time,
      day: day ? day.value : null,
      creator_id: auth.id,
    };

    if (editData?.id) {
      payload.id = editData.id;
    }

    setSaving(true);

    const request = !editData
      ? axios.post(`/organization/${organization.id}/program/${program.id}/scheduledreport`, payload)
      : axios.put(`/organization/${organization.id}/program/${program.id}/scheduledreport/${editData.id}`, payload);

    request.then((res) => {
      if (res.status == 200) {
        dispatch(flashMessage(!!editData ? 'Scheduled Report updated successfully!' : 'Report scheduled successfully!', 'alert-success', 'top'));
        setSaving(false);
        setTrigger(Math.floor(Date.now() / 1000));
        toggle();
      }
    }).catch((err) => {
      dispatch(flashMessage(<ApiErrorMessage errors={err.response.data} />, 'alert-danger', 'top'));
      setSaving(false);
    });
  };

  const editFrequency = editData?.frequency
    ? { label: editData.frequency, value: editData.frequency }
    : undefined;

  const editDay = editData?.day
    ? { label: editData.day, value: editData.day }
    : undefined;

  return (
    <Modal className="program-settings modal-lg padded" {...{ isOpen, toggle }}>
      <ModalHeader toggle={toggle} tag="h3">
        {!editData ? 'Configure Report Schedule' : 'Edit Report Schedule'}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted mb-4">Set up automated delivery for your reports.</p>
        {saving && <p className="text-muted">Processing, please wait...</p>}
        <Form
          onSubmit={onSubmit}
          keepDirtyOnReinitialize={true}
          initialValues={{
            report_name: !!editData ? {
              label: normalizeManagerReportType(editData?.report || {}).title,
              value: editData?.report?.id || editData?.report?.report_id,
            } : undefined,
            frequency:     editFrequency,
            day:           editDay,
            recipient:     editData?.recipient || '',
            delivery_time: editData?.delivery_time || '',
          }}
          validate={validate}
        >
          {({ handleSubmit, form }) => (
            <form onSubmit={handleSubmit}>
              <Row className="mt-0">
                {/* Left column — WHAT & WHEN */}
                <Col md={6}>
                  <p style={sectionTitleStyle}>What &amp; When</p>

                  <Field name="report_name">
                    {({ input, meta }) => (
                      <FormGroup>
                        <Label>Select Report</Label>
                        <Select
                          {...input}
                          options={reportOptions}
                          placeholder="Select Report..."
                        />
                        {meta.touched && meta.error && (
                          <span className="form-error">{meta.error}</span>
                        )}
                      </FormGroup>
                    )}
                  </Field>

                  <FormGroup>
                    <Label>Recurrence</Label>
                    <div className="d-flex gap-2">
                      <div className="flex-fill">
                        <Field name="frequency">
                          {({ input, meta }) => (
                            <>
                              <Select
                                {...input}
                                options={SCHEDULED_FREQUENCY}
                                placeholder="Select Frequency..."
                                onChange={(val) => {
                                  input.onChange(val);
                                  setSelectedFrequency(val);
                                  form.change('day', null);
                                }}
                              />
                              {meta.touched && meta.error && (
                                <span className="form-error">{meta.error}</span>
                              )}
                            </>
                          )}
                        </Field>
                      </div>
                      {selectedFrequency && selectedFrequency.value !== 'Daily' && (
                        <div className="flex-fill">
                          <Field name="day">
                            {({ input, meta }) => (
                              <>
                                <Select
                                  {...input}
                                  options={DAY_OPTIONS[selectedFrequency.value] || []}
                                  placeholder="Select day..."
                                />
                                {meta.touched && meta.error && (
                                  <span className="form-error">{meta.error}</span>
                                )}
                              </>
                            )}
                          </Field>
                        </div>
                      )}
                    </div>
                  </FormGroup>

                  <Field name="delivery_time">
                    {({ input, meta }) => (
                      <FormGroup>
                        <Label>Delivery Time (EST)</Label>
                        <input {...input} type="time" className="form-control" />
                        {meta.touched && meta.error && (
                          <span className="form-error">{meta.error}</span>
                        )}
                      </FormGroup>
                    )}
                  </Field>
                </Col>

                {/* Right column — WHO & HOW */}
                <Col md={6}>
                  <p style={sectionTitleStyle}>Who &amp; How</p>

                  <Field name="recipient">
                    {({ input, meta }) => (
                      <FormGroup>
                        <div className="d-flex justify-content-between align-items-baseline mb-1">
                          <Label className="mb-0">Recipients</Label>
                          <small className="text-muted">Comma separated</small>
                        </div>
                        <textarea
                          {...input}
                          className="form-control"
                          rows={6}
                          placeholder="email@example.com, another@example.com"
                        />
                        {meta.touched && meta.error && (
                          <span className="form-error">{meta.error}</span>
                        )}
                      </FormGroup>
                    )}
                  </Field>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button type="button" color="secondary" outline onClick={toggle} disabled={saving}>
                  Cancel
                </Button>
                <TemplateButton disabled={saving} type="submit" text="Save Schedule" />
              </div>
            </form>
          )}
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default connect((state) => ({
  auth:         state.auth,
  program:      state.program,
  organization: state.organization,
}))(AddEditScheduledReportsModal);
